import styled, { css, keyframes } from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

const reveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Eases a part of a form into place instead of letting it pop in.
 *
 * Used for everything that appears in answer to something the user just did: a
 * question that hangs on the answer above it, a row that was added, a file that
 * was picked.
 */
export const easeIn = css`
  animation: ${reveal} 260ms cubic-bezier(0.16, 1, 0.3, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Reveal = styled.div`
  ${easeIn}
`;

/**
 * One group of a long form, boxed off from the next one.
 *
 * Questions that run into each other are hard to scan, and a form of four or
 * five groups reads as one wall of inputs; a card with its own padding gives
 * each group a beginning and an end.
 */
export const Section = styled.section`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray['200']};
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 24px;

  ${breakpoint('md')`
    padding: 28px 32px;
  `}
`;

/** Title and description of one group, ruled off from its inputs. */
export const SectionHeader = styled.header`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray['200']};
`;

/**
 * The name of a group. The brand underline of Heading is deliberately not used
 * here: repeated once per group it crowds the text it sits under.
 */
export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  line-height: 1.3;
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.colors.gray['700']};
`;

export const SectionDescription = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.gray['500']};
  max-width: 62rem;
`;

/** What the form is about, and anything that dates or labels it. */
export const FormSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  background: ${({ theme }) => theme.colors.gray['100']};
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 24px;
`;

export const FormSummaryHeading = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.colors.gray['700']};
`;

export const FormSummaryMeta = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray['500']};
`;

/**
 * Something the reader has to take in before going on: why something was
 * rejected, or that a period closed. Coloured like the matching alert.
 */
export const FormNotice = styled.p`
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  ${({ theme, type = 'info' }) => css`
    color: ${theme.colors.alerts[type].text};
    background: ${theme.colors.alerts[type].background};
  `}
`;

/**
 * How far something got through an approval flow, read at a glance: green once
 * it was accepted, red once it was rejected, amber while it waits, grey while
 * it is still a draft.
 */
export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.bold};
  white-space: nowrap;
  ${({ status, theme }) => {
    const { alerts } = theme.colors;

    if (status === 'ACCEPTED') {
      return css`
        color: ${alerts.success.text};
        background: ${alerts.success.background};
      `;
    }

    if (status === 'REJECTED') {
      return css`
        color: ${alerts.error.text};
        background: ${alerts.error.background};
      `;
    }

    if (status === 'SUBMITTED') {
      return css`
        color: ${alerts.warning.text};
        background: ${alerts.warning.background};
      `;
    }

    if (status === 'CHAIR_APPROVED') {
      return css`
        color: ${alerts.info.text};
        background: ${alerts.info.background};
      `;
    }

    return css`
      color: ${alerts.empty.text};
      background: ${alerts.empty.background};
    `;
  }}
`;

/** The buttons that end a form, ruled off from the last group above them. */
export const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-top: 24px;
  margin-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.gray['200']};
`;
